/**
 * Macro arithmetic, shared by both clients.
 *
 * This lives here rather than in each app because a discrepancy between what
 * the web dashboard and the phone show for the same day is the kind of bug
 * users don't report — they just stop trusting the numbers.
 */

export interface Macros {
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

export const ZERO: Macros = { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 };

/** Atwater factors. Used to render the macro split, never to override a
 *  measured kcal value — a label's stated energy is authoritative even when it
 *  doesn't reconcile with its own macro breakdown, which is common. */
export const KCAL_PER_GRAM = { protein: 4, carbs: 4, fat: 9 } as const;

export function add(a: Macros, b: Macros): Macros {
  return {
    kcal: a.kcal + b.kcal,
    protein_g: a.protein_g + b.protein_g,
    carbs_g: a.carbs_g + b.carbs_g,
    fat_g: a.fat_g + b.fat_g,
  };
}

export function sum(entries: readonly Macros[]): Macros {
  return entries.reduce(add, ZERO);
}

/** Scale per-100g nutrition to an actual portion. */
export function forGrams(per100: Macros, grams: number): Macros {
  const factor = grams / 100;
  return {
    kcal: per100.kcal * factor,
    protein_g: per100.protein_g * factor,
    carbs_g: per100.carbs_g * factor,
    fat_g: per100.fat_g * factor,
  };
}

/**
 * Share of energy from each macro, as fractions summing to 1.
 *
 * Derived from the macro grams rather than the stated kcal, because those two
 * often disagree on crowd-sourced data and a ring chart that doesn't close
 * looks broken. Returns nulls when there's nothing to divide.
 */
export function energySplit(m: Macros): { protein: number; carbs: number; fat: number } | null {
  const fromProtein = m.protein_g * KCAL_PER_GRAM.protein;
  const fromCarbs = m.carbs_g * KCAL_PER_GRAM.carbs;
  const fromFat = m.fat_g * KCAL_PER_GRAM.fat;
  const total = fromProtein + fromCarbs + fromFat;
  if (total <= 0) return null;
  return { protein: fromProtein / total, carbs: fromCarbs / total, fat: fromFat / total };
}

/** Remaining against a target. Can go negative — over-budget is information,
 *  not an error, and clamping it at zero hides the thing the user wants to see. */
export function remaining(target: Macros, consumed: Macros): Macros {
  return {
    kcal: target.kcal - consumed.kcal,
    protein_g: target.protein_g - consumed.protein_g,
    carbs_g: target.carbs_g - consumed.carbs_g,
    fat_g: target.fat_g - consumed.fat_g,
  };
}
